<template>
    <v-navigation-drawer
        v-model="drawer"
        app
        temporary
        right
        :width="drawerWidth"
        class="ambience-details-panel"
    >
        <v-card flat class="fill-height d-flex flex-column">
            <v-toolbar flat color="grey lighten-4" class="flex-grow-0">
                <v-toolbar-title class="subtitle-1 font-weight-medium">
                    <v-icon left color="primary">mdi-door</v-icon>
                    Detalhes da Sala
                </v-toolbar-title>
                <v-spacer />
                <v-btn icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-divider />

            <div class="flex-grow-1 overflow-y-auto">
                <template v-if="loading">
                    <v-skeleton-loader type="image, article, actions" />
                </template>

                <template v-else-if="ambience">
                    <v-carousel
                        v-if="ambience.images && ambience.images.length"
                        height="200"
                        hide-delimiters
                        show-arrows-on-hover
                        class="grey lighten-4"
                    >
                        <v-carousel-item
                            v-for="(image, i) in ambience.images"
                            :key="i"
                            :src="image.url"
                            contain
                        />
                    </v-carousel>
                    <div v-else class="grey lighten-3 d-flex align-center justify-center" style="height: 200px">
                        <v-icon size="64" color="grey lighten-1">mdi-image-off</v-icon>
                    </div>

                    <v-card-text>
                        <div class="text-h6 mb-2">{{ ambience.name }}</div>
                        
                        <v-chip
                            v-if="ambience.type"
                            small
                            outlined
                            :color="ambience.type.is_virtual ? 'purple' : 'blue'"
                            class="mb-3"
                        >
                            <v-icon small left>
                                {{ ambience.type.is_virtual ? 'mdi-monitor-dashboard' : 'mdi-door' }}
                            </v-icon>
                            {{ ambience.type.name }}
                        </v-chip>

                        <v-divider class="my-3" />

                        <div class="subtitle-2 mb-2">
                            <v-icon small left color="grey">mdi-information</v-icon>
                            Informações Gerais
                        </div>

                        <v-simple-table dense>
                            <tbody>
                                <tr v-if="ambience.code">
                                    <td class="caption grey--text" width="40%">Código</td>
                                    <td class="body-2">{{ ambience.code }}</td>
                                </tr>
                                <tr v-if="ambience.block">
                                    <td class="caption grey--text">Bloco</td>
                                    <td class="body-2">{{ ambience.block.name }}</td>
                                </tr>
                                <tr v-if="ambience.floor">
                                    <td class="caption grey--text">Andar</td>
                                    <td class="body-2">{{ ambience.floor.name }}</td>
                                </tr>
                                <tr v-if="ambience.campus">
                                    <td class="caption grey--text">Campus</td>
                                    <td class="body-2">{{ ambience.campus.name }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>

                        <v-divider class="my-3" />

                        <div class="subtitle-2 mb-2 d-flex align-center">
                            <v-icon small left color="grey">mdi-account-group</v-icon>
                            Capacidade
                            <v-chip
                                v-if="activeCapacityLabel"
                                x-small
                                color="primary"
                                dark
                                class="ml-2"
                            >
                                Modo ativo: {{ activeCapacityLabel }}
                            </v-chip>
                        </div>

                        <v-row dense>
                            <v-col cols="4" class="text-center">
                                <div
                                    class="capacity-cell"
                                    :class="{ 'capacity-cell--active': ambience.capacity_mode === 'RED' }"
                                >
                                    <div class="text-h5 purple--text">{{ ambience.reduced_capacity || 0 }}</div>
                                    <div class="caption grey--text">Reduzida</div>
                                </div>
                            </v-col>
                            <v-col cols="4" class="text-center">
                                <div
                                    class="capacity-cell"
                                    :class="{ 'capacity-cell--active': !ambience.capacity_mode || ambience.capacity_mode === 'CONV' }"
                                >
                                    <div class="text-h5 success--text">{{ ambience.capacity || 0 }}</div>
                                    <div class="caption grey--text">Convencional</div>
                                </div>
                            </v-col>
                            <v-col cols="4" class="text-center">
                                <div
                                    class="capacity-cell"
                                    :class="{ 'capacity-cell--active': ambience.capacity_mode === 'AMP' }"
                                >
                                    <div class="text-h5 primary--text">{{ ambience.extended_capacity || 0 }}</div>
                                    <div class="caption grey--text">Ampliada</div>
                                </div>
                            </v-col>
                        </v-row>

                        <v-divider class="my-3" v-if="occupation" />

                        <template v-if="occupation">
                            <div class="subtitle-2 mb-2">
                                <v-icon small left color="grey">mdi-chart-bar</v-icon>
                                Ocupação Atual
                            </div>

                            <v-progress-linear
                                :value="occupationPercentage"
                                :color="occupationColor"
                                height="24"
                                rounded
                                class="mb-2"
                            >
                                <template v-slot:default>
                                    <strong class="white--text">{{ occupationPercentage }}%</strong>
                                </template>
                            </v-progress-linear>

                            <v-row dense>
                                <v-col cols="6">
                                    <v-card outlined class="pa-2 text-center">
                                        <div class="text-h6">{{ occupation.students || 0 }}</div>
                                        <div class="caption grey--text">Alunos Atuais</div>
                                    </v-card>
                                </v-col>
                                <v-col cols="6">
                                    <v-card outlined class="pa-2 text-center">
                                        <div class="text-h6">{{ occupation.forecast || 0 }}</div>
                                        <div class="caption grey--text">Previsão</div>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <v-alert
                                v-if="occupation.balance !== undefined"
                                :type="occupation.balance >= 0 ? 'success' : 'error'"
                                dense
                                text
                                class="mt-3"
                            >
                                <strong>Saldo:</strong> {{ occupation.balance > 0 ? '+' : '' }}{{ occupation.balance }} vagas
                            </v-alert>
                        </template>

                        <v-divider class="my-3" v-if="ambience.resources && ambience.resources.length" />

                        <template v-if="ambience.resources && ambience.resources.length">
                            <div class="subtitle-2 mb-2">
                                <v-icon small left color="grey">mdi-devices</v-icon>
                                Recursos Disponíveis
                            </div>

                            <v-chip-group column>
                                <v-chip
                                    v-for="resource in ambience.resources"
                                    :key="resource.id"
                                    small
                                    outlined
                                >
                                    <v-icon small left>{{ resource.icon || 'mdi-check' }}</v-icon>
                                    {{ resource.name }}
                                </v-chip>
                            </v-chip-group>
                        </template>
                    </v-card-text>
                </template>

                <template v-else>
                    <v-card-text class="text-center py-8">
                        <v-icon size="64" color="grey lighten-1">mdi-door-closed</v-icon>
                        <div class="grey--text mt-2">Selecione uma sala para ver os detalhes</div>
                    </v-card-text>
                </template>
            </div>

            <v-divider />

            <v-card-actions class="pa-3 flex-grow-0">
                <v-btn
                    v-if="ambience"
                    color="orange"
                    outlined
                    small
                    @click="$emit('click:suggest-swap', ambience)"
                >
                    <v-icon small left>mdi-swap-horizontal</v-icon>
                    Sugerir Troca
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="ambience"
                    color="primary"
                    small
                    @click="$emit('click:edit', ambience)"
                >
                    <v-icon small left>mdi-pencil</v-icon>
                    Editar
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-navigation-drawer>
</template>

<script>
import {
    getActiveCapacity,
    getCapacityModeLabel,
    getOccupationColor,
    getOccupationPercentage,
} from './helpers/occupationStatus'

export default {
    name: "SysAmbienceDetailsSidePanel",

    data: () => ({
        drawer: false
    }),

    props: {
        show: {
            type: Boolean,
            default: false
        },
        ambience: {
            type: Object,
            default: null
        },
        occupation: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        filters: {
            type: Object,
            default: () => ({})
        }
    },

    computed: {
        drawerWidth() {
            switch (this.$vuetify.breakpoint.name) {
                case 'xs':
                    return '100%'
                case 'sm':
                    return '85%'
                default:
                    return 450
            }
        },

        occupationPercentage() {
            return Math.min(getOccupationPercentage(this.ambience, this.occupation), 100)
        },

        occupationColor() {
            return getOccupationColor(this.ambience, this.occupation)
        },

        activeCapacityValue() {
            return getActiveCapacity(this.ambience)
        },

        activeCapacityLabel() {
            return getCapacityModeLabel(this.ambience?.capacity_mode)
        }
    },

    watch: {
        show(newVal) {
            this.drawer = newVal
        },
        drawer(newVal) {
            this.$emit('update:show', newVal)
        }
    },

    methods: {
        close() {
            this.drawer = false
        }
    }
}
</script>

<style scoped>
.ambience-details-panel {
    z-index: 1000;
}

.fill-height {
    height: 100%;
}

.overflow-y-auto {
    overflow-y: auto;
}

.capacity-cell {
    padding: 8px 4px;
    border-radius: 6px;
    border: 2px solid transparent;
    transition: all 0.2s ease;
}

.capacity-cell--active {
    border-color: #1976D2;
    background-color: rgba(25, 118, 210, 0.08);
}
</style>
